import React, { useEffect, useMemo, useState } from "react";
import { X, Loader2, ArrowRightLeft, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";
import { getDatabaseShares, getDatabaseTransferPreview, transferDatabase } from "../../../api/database";

const TEAL = "#55c5d0";

const apiError = (e, fallback) => {
  const d = e?.response?.data || {};
  return d.detail || d.to_agent || d.from_agent || d.error || fallback;
};

/**
 * Hand one agent's whole Database to another agent. Super admin only.
 *
 * Christian Gravias, Monday 13202305277: "We have a real estate agent leaving
 * and we want to transfer his data to another agents database." Every owner,
 * buyer, tab and extra column moves; a tab the receiving agent already has
 * (same category and name) is merged into theirs. The super admin can pick
 * themselves as the receiver.
 */
export default function TransferDatabaseModal({ fromAgent, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [agents, setAgents] = useState([]);
  const [toId, setToId] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    Promise.all([getDatabaseTransferPreview(fromAgent.id), getDatabaseShares()])
      .then(([p, s]) => {
        setPreview(p.data);
        const list = [...(s.data?.agents || [])];
        if (s.data?.me) list.unshift({ ...s.data.me, isMe: true });
        setAgents(list.filter((a) => String(a.id) !== String(fromAgent.id)));
      })
      .catch((e) => toast.error(apiError(e, "Couldn't load the transfer details")))
      .finally(() => setLoading(false));
  }, [fromAgent.id]);

  const target = useMemo(() => agents.find((a) => String(a.id) === String(toId)), [agents, toId]);
  const targetName = target ? (target.isMe ? "you" : target.name || target.email) : "";

  const submit = async () => {
    if (!target || !confirmed) return;
    setSaving(true);
    try {
      const res = await transferDatabase(fromAgent.id, target.id);
      const n = res.data?.records ?? 0;
      toast.success(`${n} record${n === 1 ? "" : "s"} moved to ${target.isMe ? "your" : `${target.name || target.email}'s`} Database`);
      onDone?.(target);
    } catch (e) {
      toast.error(apiError(e, "Couldn't transfer the database"));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[80] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[480px] max-w-full max-h-[88vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <ArrowRightLeft size={16} style={{ color: TEAL }} />
              Transfer {fromAgent.name || "this agent"}'s Database
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Moves every owner, buyer, list and column to another agent. Use it when an agent leaves.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 mt-0.5">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : (
            <>
              {preview && (
                <p className="text-sm text-gray-700">
                  <b>{preview.owners}</b> owners, <b>{preview.buyers}</b> buyers and{" "}
                  <b>{preview.lists}</b> lists will move.
                </p>
              )}
              <label className="block">
                <span className="text-xs font-bold text-gray-600">Move them to</span>
                <select
                  value={toId}
                  onChange={(e) => setToId(e.target.value)}
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Choose an agent…</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.isMe ? `Me (${a.name || a.email})` : a.name || a.email}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 text-xs">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  Lists with the same category and name as one {targetName || "the new agent"} already has are merged
                  into theirs. {fromAgent.name || "This agent"}'s sharing settings are removed and their Database
                  will be empty afterwards. Notes keep their original author.
                </span>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                I understand this moves the whole Database{targetName ? ` to ${targetName}` : ""}.
              </label>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!target || !confirmed || saving}
            className="px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50 flex items-center gap-1.5"
            style={{ background: TEAL }}
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Transfer Database
          </button>
        </div>
      </div>
    </div>
  );
}
